// FaceAnimWizard — face animation upload. Accepts either a face JSON
// (already-converted ARKit weights) or a GLB the artist tagged as "face".

import React, { useEffect, useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'

function baseName(file) {
  return (file?.name ?? '').replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_\-]+/g, '_')
}

export default function FaceAnimWizard({ file, path = [], onDone, onCancel }) {
  const [name, setName] = useState(baseName(file))
  const [durationMs, setDurationMs] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [frameCount, setFrameCount] = useState(null)

  useEffect(() => {
    if (!file) return
    if (file.name.toLowerCase().endsWith('.json')) {
      file.text().then(t => {
        try {
          const data = JSON.parse(t)
          const frames = Array.isArray(data?.frames) ? data.frames.length : (Array.isArray(data) ? data.length : null)
          const fps = data?.fps ?? 60
          if (frames) {
            setFrameCount(frames)
            setDurationMs(Math.round((frames / fps) * 1000))
          }
        } catch { /* ignore — submit will surface the error */ }
      })
    }
  }, [file])

  const submit = async () => {
    if (!name.trim()) { setError('Name is required'); return }
    setSubmitting(true); setError(null)
    try {
      const row = await CustomAssetManager.uploadAnimationAsset(file, {
        name: name.trim(),
        kind: 'face',
        category: 'other',
        durationMs,
      })
      assetRegistry.setPath(`anim:${row.id}`, path)
      onDone?.(row)
    } catch (e) {
      setError(String(e.message ?? e))
      setSubmitting(false)
    }
  }

  return (
    <WizardShell
      title="New face animation"
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={submit}
      submitDisabled={submitting || !name.trim()}
      submitLabel={submitting ? 'Uploading…' : 'Upload'}
      error={error}
    >
      <label style={labelStyle}>Name</label>
      <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} autoFocus />

      <div style={{ marginTop: 10, fontSize: 10, fontFamily: 'var(--m-mono)', color: tokens.dim }}>
        {frameCount != null ? `${frameCount} frames · ${(durationMs/1000).toFixed(2)}s` : 'Preview: face clips play through FaceClipPlayer.manualPlay at runtime.'}
      </div>
    </WizardShell>
  )
}
